<template>
  <div>
    <NuxtLoadingIndicator color="#504e8b" />
    <NuxtLayout :name="layoutName">
      <NuxtPage />
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import { GTM_ID } from '~/utils/const'
import pushToGaDataLayer from '~/utils/pushToGaDataLayer'

import type {
  TypeDynamicStringSkeleton,
  TypeLocalizationStringSkeleton,
} from './types/contentful'
import type { ISsrLogCustomPayload } from './types/custom'

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ketch: any
  }
}

const { $client } = useNuxtApp()
const { locale } = useI18n()
const { setLocalizedStrings } = useLocalizedStrings()
const { setDynamicStrings } = useDynamicStrings()
const route = useRoute()
const layoutName = computed(() =>
  /(^|\/)preview(\/|$)/.test(route.path) ? 'preview' : undefined,
)

// START USER DATA
const userStore = useUserStore()
const { stores, gaUserId } = storeToRefs(userStore)
const store_id = computed(() =>
  stores.value
    .map((store) => store?.id)
    .filter(Boolean)
    .join('_')
    .toString(),
)
// END USER DATA

const { data: localizedStringsResponse } = await useAsyncData(
  `localized-strings-${locale.value}`,
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeLocalizationStringSkeleton>(
        {
          content_type: 'localizationString',
          limit: 500,
          locale: locale.value,
        },
      )

    return response?.items?.reduce((obj: Record<string, string>, entry) => {
      const key = entry?.fields?.id || ''
      const value = entry?.fields?.string || ''
      if (key && value) {
        return {
          ...obj,
          [key]: value,
        }
      }
      return obj
    }, {})
  },
  {
    transform(input) {
      return {
        strings: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => locale.value],
  },
)

if (localizedStringsResponse.value?.strings)
  setLocalizedStrings(localizedStringsResponse.value?.strings)

const { data: dynamicStringsResponse } = await useAsyncData(
  `dynamic-strings-${locale.value}`,
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeDynamicStringSkeleton>(
        {
          content_type: 'dynamicString',
          limit: 500,
          locale: locale.value,
        },
      )

    return response?.items?.reduce((obj: Record<string, string>, entry) => {
      const key = entry?.fields?.id || ''
      const value = entry?.fields?.string || ''
      if (key && value) {
        return {
          ...obj,
          [key]: value,
        }
      }
      return obj
    }, {})
  },
  {
    transform(input) {
      return {
        strings: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => locale.value],
  },
)

if (dynamicStringsResponse.value?.strings)
  setDynamicStrings(dynamicStringsResponse.value?.strings)

// GA4 login event via pending cookie
const gaPendingEvent = useCookie('ga_pending_event')
if (import.meta.client && gaPendingEvent.value === 'login') {
  pushToGaDataLayer({
    event: 'ga4.trackEvent',
    eventName: 'login',
    eventParams: { method: 'WOTC' },
  })
  gaPendingEvent.value = null
}

// Google Analytics
// REMOVED 2026-04-28 - GA4 fires through GTM
// const route = useRoute()
// watch(route, () => {
//   window?.gtag?.('config', UA_ID)
// })

watch(localizedStringsResponse, (v) => {
  if (v?.strings) setLocalizedStrings(v.strings)
})

watch(dynamicStringsResponse, (v) => {
  if (v?.strings) setDynamicStrings(v.strings)
})

onMounted(() => {
  type BrowserLog = 'log' | 'info' | 'warn' | 'error'

  const fn: Record<BrowserLog, (...a: unknown[]) => void> = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  }

  const es = new EventSource('/api/logs')

  es.addEventListener('message', (e) => {
    const { type, args, path } = JSON.parse(e.data) as ISsrLogCustomPayload

    switch (type) {
      case 'log':
      case 'info':
      case 'warn':
      case 'error':
        fn[type](`[${path}]`, ...args)
        break
      case 'fatal':
        console.error(`[${path}]`, ...args)
        break
      default:
        // silent, success, verbose, whatever—dump everything else
        console.debug(`[${path}]`, type, ...args)
    }
  })
})

useHead({
  script: [
    // Google Tag Manager
    // REMOVED 2026-04-28 - GA4 fires through GTM now
    // {
    //   async: true,
    //   src: `https://www.googletagmanager.com/gtag/js?id=${UA_ID}`,
    // },
    // User StoreID and user_id for GTM dataLayer
    ...(store_id.value || gaUserId.value
      ? [
          {
            innerHTML: `
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
          ${gaUserId.value ? `user_id: '${gaUserId.value}',` : ''}
          ${store_id.value ? `store_id: '${store_id.value}',` : ''}
        })
        `,
          },
        ]
      : []),
    // GTM container
    {
      innerHTML: `
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
          j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','${GTM_ID}');
        `,
    },
    // Geo Targetly
    {
      innerHTML: `
        (function(g,e,o,t,a,r,ge,tl,y){
          s=function(){var def='geotargetlygeocontent1635526503129_default',len=g.getElementsByClassName(def).length;
          if(len>0){for(i=0;i<len;i++){g.getElementsByClassName(def)[i].style.display='inline';}}};
          t=g.getElementsByTagName(e)[0];y=g.createElement(e);
          y.async=true;y.src='https://g1584674682.co/gc?refurl='+g.referrer+'&id=-MnByTeEa6jbEDSNSDCJ&winurl='+encodeURIComponent(window.location);
          t.parentNode.insertBefore(y,t);y.onerror=function(){s()};
        })(document,'script');
        `,
    },
    // Ketch Cookie Consent
    {
      innerHTML: `
          !function() {
            window.semaphore = window.semaphore || [];
            window.ketch = function() {
              window.semaphore.push(arguments);
            };
            var e = document.createElement("script");
            e.type = "text/javascript";
            e.src = "https://global.ketchcdn.com/web/v3/config/hasbro/wpn/boot.js";
            e.defer = e.async = true;
            document.getElementsByTagName("head")[0].appendChild(e);
          }();
        `,
      type: 'text/javascript',
    },
  ],
  noscript: [
    {
      innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
    },
    {
      innerHTML: `<style>.geotargetlygeocontent1635526503129_default{display:inline !important}</style>`,
    },
  ],
})
</script>
